import React from 'react';
import { useIntl } from 'react-intl';
import { isDev } from '@utils/is-dev';
import dayjs from 'dayjs';
import styled from 'styled-components';
import * as yup from 'yup';

import FormButton from '@components/form/form-button';
import Form from '@components/form/form';
import FormDatePickerField from '@components/form/form-date-picker-field';
import ButtonNext from '@domain/button-next';
import { useVismaConfiguration } from '@services/visitor-management';

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 24px;
`;

const FormArea = styled.div`
  background: ${({ theme }) => theme.white};
  border-radius: 30px;
  width: 50vw;
  padding: 36px;
  @media only screen and (max-width: 992px) {
    width: 80vw;
    padding: 24px;
  }
`;

const FormHeader = styled.h1`
  font-size: 36px;
  font-weight: 600;
  text-align: center;
  margin-top: 20px;
  color: ${({ theme }) => theme.text};
  @media only screen and (max-width: 768px) {
    font-size: 24px;
    margin-top: 0;
  }
`;

export interface DobFormValues {
  birthDate: string;
}

const defaultValues = {
  birthDate: isDev ? dayjs('1998-10-14') : ''
} as { birthDate: string };

interface DobFormContentProps {
  isPending?: boolean;
  onBack?: () => void;
}

function DobFormContent({ isPending, onBack }: DobFormContentProps) {
  const intl = useIntl();
  const { configuration } = useVismaConfiguration();

  return (
    <>
      <FormDatePickerField
        name="birthDate"
        label={intl.formatMessage({ id: 'DATE_OF_BIRTH' })}
        format="YYYY-MM-DD"
        fullWidth
        required
        style={{ marginTop: 30 }}
      />
      <ButtonNext
        htmlType="submit"
        fullWidth
        withRightArrow
        isLoading={isPending}
        style={{ marginTop: 30 }}
      />
      <FormButton
        type="default"
        fullWidth
        onClick={onBack}
        withLeftArrow
        variant="outlined"
        style={{ marginTop: 12 }}
      >
        {configuration?.generalInformation?.button_cancel}
      </FormButton>
    </>
  );
}

interface DobFeatureProps {
  onSubmit: (values: DobFormValues) => Promise<void>;
  isPending?: boolean;
  title?: string;
  onBack?: () => void;
}

function DobFeature({ onSubmit, isPending, title, onBack }: DobFeatureProps) {
  const intl = useIntl();
  const schema = yup.object({
    birthDate: yup
      .string()
      .required(intl.formatMessage({ id: 'DOB_REQUIRED_ERROR' }))
  });

  return (
    <Container>
      <FormArea>
        <Form schema={schema} defaultValues={defaultValues} onSubmit={onSubmit}>
          <FormHeader>
            {title ?? intl.formatMessage({ id: 'DOB_VERIFICATION_HEADER' })}
          </FormHeader>
          <DobFormContent isPending={isPending} onBack={onBack} />
        </Form>
      </FormArea>
    </Container>
  );
}

export default DobFeature;
